import React, { useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import compose from 'lodash/flowRight';
import { lifecycle } from 'recompose';
import merge from 'lodash/merge';
import flatten from 'lodash/flatten';
import map from 'lodash/map';
import uniq from 'lodash/uniq';
import omit from 'lodash/omit';
import memoize from 'memoize-one';
import Promise from 'bluebird';
import ObjectID from 'bson-objectid';

import withi18n from 'weego-common/src/hoc/i18n';
import tryJsonParse from 'weego-common/src/utils/tryJsonParse';
import momentTz from 'weego-common/src/providers/moment';

import client from '../../providers/weego-client';

import CRUDManager from '../common/CRUDManager';
import transactionsActions from '../../actions/transactions';
import accountsActions from '../../actions/accounts';
import accountLabel from '../../utils/accountLabel';
import reverse from 'lodash/reverse';
import sortBy from 'lodash/sortBy';
import dateToString from '../../utils/date/dateToString';
import vehicleLabel from '../../utils/drivers/vehicleLabel';
import driverLabel from '../../utils/driverLabel';
import stopLabel from '../../utils/stopLabel';
import { DatesRangeInput } from 'semantic-ui-calendar-react';

const renderPaymentAccount = (paymentAccount, userAccount) => {
  if (!paymentAccount) {
    return null;
  }
  if (paymentAccount.userId === 'MASTER') {
    return 'Weego';
  }
  if (userAccount) {
    return accountLabel(userAccount, { displayPhoneNumber: true }) || 'N/A';
  }
  return paymentAccount.userId;
};

const fields = [
  {
    key: 'id',
    label: 'Identifiant',
    type: 'STRING',
  },
  {
    key: 'subscription.metadata.subscriptionCardNumber',
    label: 'Abonnement',
    type: 'STRING',
    render: ({ record: transaction }) => {
      return transaction?.subscription?.package?.label &&
        transaction?.subscription?.metadata?.subscriptionCardNumber
        ? transaction.subscription.package.label +
            ' / ' +
            transaction.subscription.metadata.subscriptionCardNumber
        : 'N/A';
    },
  },
  {
    key: 'type',
    label: 'Type',
    type: 'ENUM',
    options: [
      {
        value: 'TICKETING',
        label: 'Billeterie',
      },
      {
        value: 'SUBSCRIPTION',
        label: 'Abonnement',
      },
    ],
  },
  {
    key: 'fullName',
    label: 'Passager',
    refId: '_id',
    type: 'REF',
    ref: 'accounts',
    render: ({ record: transaction }) => {
      return transaction?.subscription?.metadata
        ? `${transaction?.subscription?.metadata.firstName || ''} ${
            transaction?.subscription?.metadata.lastName || ''
          } \n ${transaction?.subscription?.metadata.userPhoneNumber || ''}`
        : 'N/A';
    },
    disableFilter: true,
  },
  {
    key: 'amount',
    label: 'Montant',
    type: 'NUMBER',
    render: ({ record: transaction }) => {
      return (
        transaction.amount ||
        tryJsonParse(transaction.metadata)?.busLine?.ticketPrice?.value
      );
    },
  },
  {
    key: 'createdAt',
    label: 'Date',
    type: 'DATETIME',
    render({ record }) {
      if (record.type === 'TICKETING') {
        const metadata = tryJsonParse(record.metadata);
        if (metadata?.departureTime) {
          return dateToString(metadata.departureTime);
        }
      }
    },
  },
  {
    key: 'metadata.qrCode.token',
    label: 'Statut',
    type: 'STRING',
    render({ record }) {
      if (record.type === 'TICKETING') {
        const metadata = tryJsonParse(record.metadata);
        if (metadata?.qrCode) {
          return metadata.qrCode.token ? 'Réglé' : 'Erreur';
        } else {
          return 'En attente';
        }
      }
    },
  },
];
const resourceLabel = 'ticket';

const getRelations = memoize(accounts => ({ accounts }));

const style = { height: 'calc(100% - 80px)' };

const mapStateToProps = state => ({
  recordKey: 'id',
  records: state.transactions,
  relations: getRelations(state.accounts),
  resourceLabel,
  fields,
  style,
  ...state.transactionsMeta,
  error: state.transactionsMeta.error,
  canEdit: false,
  canRemove: false,
  canExport: true,
  canAdd: false,
});

const mapDispatchToProps = dispatch => ({
  fetchStart(payload) {
    if (payload.search) {
      dispatch(
        accountsActions.fetchStart({
          search: payload.search,
          callback: searchResultAccounts => {
            const accountIds = searchResultAccounts.map(account => account._id);
            dispatch(
              transactionsActions.fetchStart(
                merge({}, omit(payload, 'search'), {
                  where: {
                    status: payload.where?.status || {
                      inq: ['SETTLED', 'ERRORED', 'REFUNDED'],
                    },
                    type: { inq: ['TICKETING'] },
                    or: [
                      {
                        '$from.userId$': {
                          inq: accountIds,
                        },
                      },
                      {
                        '$to.userId$': {
                          inq: accountIds,
                        },
                      },
                    ],
                  },
                  query: {
                    filter: {
                      include: ['from', 'to', 'subscription'],
                    },
                  },
                }),
              ),
            );
          },
        }),
      );
    } else {
      dispatch(
        transactionsActions.fetchStart(
          merge({}, payload, {
            where: {
              status: payload.where?.status || {
                inq: ['SETTLED', 'ERRORED', 'REFUNDED'],
              },
              type: {
                inq: payload?.where?.type?.inq || ['TICKETING'],
              },
              // and: [
              //   {
              //     createdAt: {
              //       lte:
              //         payload?.where?.createdAt?.lte ||
              //         momentTz().add(5, 'day').toDate(),
              //     },
              //   },
              //   {
              //     createdAt: {
              //       gte:
              //         payload?.where?.createdAt?.gte ||
              //         momentTz().subtract(5, 'day').toDate(),
              //     },
              //   },
              // ],
            },
            query: {
              filter: {
                include: { all: true, nested: true },
              },
            },
          }),
        ),
      );
    }
  },
  createStart: compose(dispatch, transactionsActions.createStart),
  updateStart: compose(dispatch, transactionsActions.updateStart),
  updateSuccess: compose(dispatch, transactionsActions.updateSuccess),
  deleteStart: compose(dispatch, transactionsActions.deleteStart),
  fetchAccounts: compose(dispatch, accountsActions.fetchStart),
});

const enhance = compose(
  withi18n('transactions'),
  connect(mapStateToProps, mapDispatchToProps),
  lifecycle({
    componentDidUpdate(prevProps) {
      if (this.props.records !== prevProps.records) {
        const transactions = this.props.records;
        const userIds = uniq(
          flatten(map(transactions, t => [t.from?.userId, t.to?.userId]))
            .filter(v => !!v)
            .filter(v => ObjectID.isValid(v)),
        );
        this.props.fetchAccounts({
          where: {
            _id: {
              inq: userIds,
            },
          },
        });
      }
    },
  }),
);

const TransactionsManager = ({
  records,
  fetchStart,
  createStart,
  updateSuccess,
  creating,
  createError,
  t,
  fields,
  ...props
}) => {
  const [currentOrder, setCurrentOrder] = useState(null);

  const [ticketingTrips, setTicketingTrips] = useState([]);
  const fieldsWithMetadataField = useMemo(() => {
    return fields.concat({
      key: 'metadata',
      label: 'Ligne',
      type: 'STRING',
      render({ record: transaction, serialize }) {
        if (transaction.type === 'TICKETING') {
          const metadata = tryJsonParse(transaction.metadata);
          if (!metadata) {
            return null;
          }
          if (metadata.busLine) {
            return (
              metadata.busLine.label ||
              `${metadata.busLine.company} ${metadata.busLine.number}`.trim()
            );
          }
          const trip = ticketingTrips.find(
            t =>
              t.id === metadata.tripId &&
              t.departureTime === metadata.departureTime,
          );
          if (!trip) {
            return null;
          }
          const demandId = metadata.demandId;
          const allStops = [trip.from, ...(trip.stops || []), trip.to];
          const pickupStop = allStops.find(s =>
            s.demandIds?.includes(demandId),
          );
          const dropoffStop = reverse(allStops).find(s =>
            s.demandIds?.includes(demandId),
          );
          const { driver, vehicle } = trip;
          const stopInfos =
            pickupStop && dropoffStop
              ? `${stopLabel(pickupStop)}@${momentTz(pickupStop.maxDate).format(
                  'HH:mm',
                )}\n${stopLabel(dropoffStop)}@${momentTz(
                  dropoffStop.maxDate,
                ).format('HH:mm')}`
              : '';
          const tripInfos = `${vehicleLabel(vehicle)} - ${driverLabel(driver)}`;
          if (serialize) {
            return `${stopInfos}\n${tripInfos}`;
          }
          return (
            <span style={{ whiteSpace: 'pre' }}>
              {`${stopInfos}\n${tripInfos}`}
            </span>
          );
        } else if (
          transaction.type === 'VOUCHER_REDEEM' ||
          transaction.type === 'VOUCHER_REDEEM_ROLLBACK'
        ) {
          const metadata = tryJsonParse(transaction.metadata);
          return `Code: ${metadata?.voucherCode}`;
        } else if (transaction.type === 'REFUND') {
          const metadata = tryJsonParse(transaction.metadata);
          return `Raison: ${metadata?.motive} / ${metadata.targetTransactionId}`;
        }
        return null;
      },
    });
  }, [fields, ticketingTrips]);

  const wasCreating = useRef(creating);
  // Transactions records need to have a custom sort because of redux-crud default store sorting
  const localSortedRecords = useMemo(
    () =>
      currentOrder
        ? currentOrder.includes('DESC')
          ? reverse(sortBy(records, currentOrder.split(' ')[0]))
          : sortBy(records, currentOrder.split(' ')[0])
        : records,
    [currentOrder, records],
  );

  useEffect(() => {
    async function fetchTicketingTrips() {
      const ticketingMetadatas = map(records, t => {
        if (t.type !== 'TICKETING') {
          return null;
        }
        const metadata = tryJsonParse(t.metadata);
        if (!metadata) {
          return null;
        }
        return metadata;
      }).filter(v => !!v);
      const trips = await Promise.map(ticketingMetadatas, async metadata => {
        const data = await client.rideBooking.getTripsInPeriod(
          {
            start: momentTz(metadata.departureTime).startOf('day').toDate(),
            end: momentTz(metadata.departureTime).endOf('day').toDate(),
          },
          {
            where: {
              id: metadata.tripId,
            },
            include: ['vehicle', 'driver'],
            isDeleted: true,
          },
          true,
        );
        return data.splitTrips[0];
      });
      setTicketingTrips(trips.filter(v => !!v));
    }
    fetchTicketingTrips();
  }, [records]);

  useEffect(() => {
    wasCreating.current = creating;
  }, [creating]);

  const [datesRange, setDatesRange] = useState(
    `${momentTz().startOf('month').format('YYYY-MM-DD')}-${momentTz()
      .endOf('month')
      .format('YYYY-MM-DD')}`,
  );
  const [datesRangeDate, setDatesRangeDate] = useState({
    start: momentTz().startOf('month').toDate(),
    end: momentTz().endOf('month').toDate(),
  });

  const handleFromTo = (e, { name, value }) => {
    setDatesRange(value);
    const range = value;
    const dates = range.split(' - ');
    if (dates.length === 2 && !!dates[1]) {
      const start = momentTz(dates[0], 'YYYY-MM-DD').startOf('day').toDate();
      const end = momentTz(dates[1], 'YYYY-MM-DD').endOf('day').toDate();
      setDatesRangeDate({ start, end });
    }
  };

  return (
    <div style={style}>
      <CRUDManager
        recordActions={[]}
        records={localSortedRecords}
        dateRangeFilter={datesRangeDate}
        fetchStart={payload => {
          const payloadWithDefaultOrder = {
            ...payload,
            order: payload.order || 'createdAt DESC',
          };
          setCurrentOrder(payloadWithDefaultOrder.order);
          fetchStart(payloadWithDefaultOrder);
        }}
        fields={fieldsWithMetadataField}
        createStart={() => {}}
        creating={false}
        renderAfterFilters={() => {
          return (
            <DatesRangeInput
              allowSameEndDate
              dateFormat="YYYY-MM-DD"
              iconPosition="left"
              value={datesRange}
              placeholder="x- To"
              onChange={handleFromTo}
              name="fromTo"
            />
          );
        }}
        {...props}
      />
    </div>
  );
};

export default enhance(TransactionsManager);
